import { RefreshCw, Activity, Database, FileText, Clock3, Info } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

const snapshots = new WeakMap();
const icons = { analyses: Activity, sql: Database, reports: FileText };
const fields = [
  ['analyses', 'Concurrent chat analyses', 32],
  ['sql', 'Concurrent SQL queries', 64],
  ['reports', 'Concurrent report generation', 16],
  ['sql_wait_seconds', 'SQL wait timeout (seconds)', 120],
];
const message = error => error.response?.data?.detail?.message ||
  (typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Could not load or save capacity. Try refreshing.');

export default function CompanyCapacity({ api, onDirtyChange, onSaved }) {
  const [data, setData] = useState(() => snapshots.get(api) || null), [draft, setDraft] = useState(() => snapshots.get(api)?.limits || null);
  const [version, setVersion] = useState(() => snapshots.get(api)?.version ?? null), [error, setError] = useState('');
  const [busy, setBusy] = useState(false), [notice, setNotice] = useState('');
  const mounted = useRef(false), inFlight = useRef(false);
  const editState = useRef(false), saving = useRef(false), generation = useRef(0);
  const dirty = !!draft && fields.some(([key]) => draft[key] !== data?.limits[key]);
  editState.current = dirty;
  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => {
    let active = true;
    mounted.current = true;
    const cached = snapshots.get(api);
    setData(cached || null); setDraft(cached?.limits || null); setVersion(cached?.version ?? null);
    editState.current = false;
    async function poll() {
      if (document.hidden || inFlight.current || saving.current) return;
      const request = generation.current;
      inFlight.current = true;
      try {
        const result = await api.capacity();
        if (active && request === generation.current) {
          snapshots.set(api, result);
          setData(result);
          // Clean forms follow current settings; in-progress edits keep their
          // original version so a concurrent administrator cannot be overwritten.
          if (!editState.current) { setDraft(result.limits); setVersion(result.version); }
          setError('');
        }
      } catch (e) { if (active && request === generation.current) setError(message(e)); }
      finally { inFlight.current = false; }
    }
    poll();
    const timer = setInterval(poll, 15000);
    document.addEventListener('visibilitychange', poll);
    return () => { active = false; mounted.current = false; clearInterval(timer); document.removeEventListener('visibilitychange', poll); };
  }, [api]);
  const reload = async () => {
    const request = ++generation.current;
    setBusy(true); setError('');
    try {
      const result = await api.capacity();
      if (mounted.current && request === generation.current) { snapshots.set(api, result); setData(result); setDraft(result.limits); setVersion(result.version); }
    } catch (e) { if (mounted.current) setError(message(e)); }
    finally { if (mounted.current) setBusy(false); }
  };
  const save = async event => {
    saving.current = true; generation.current += 1;
    event.preventDefault(); setBusy(true); setError(''); setNotice('');
    try {
      const result = await api.saveCapacity({ version, limits: draft });
      if (!mounted.current) return;
      setVersion(result.version); setDraft(result.limits);
      setData(current => ({...current, limits: result.limits}));
      setNotice('Limits saved. Running work continues; new admissions use the updated limits.');
      onSaved();
      await reload();
    } catch (e) { if (mounted.current) setError(message(e)); }
    finally { saving.current = false; if (mounted.current) setBusy(false); }
  };
  return <section className="company-capacity" aria-busy={busy || (!data && !error)}>
    <div className="policy-section-heading"><div><h2>Workload capacity</h2><p>Monitor active work and manage how much this company can run at once.</p></div>
      <button disabled={busy} onClick={() => { if (!dirty || window.confirm('Discard unsaved limit changes and reload?')) reload(); }}><RefreshCw size={14} aria-hidden="true"/>Refresh capacity</button></div>
    {error && <p role="alert" className="policy-error">{error}</p>}
    {notice && <p role="status" className="policy-notice">{notice}</p>}
    {!data ? <p role="status" className="capacity-loading">{!error && <span className="policy-loader"/>}{error ? 'Capacity unavailable.' : 'Loading capacity…'}</p> : <>
      <div className="capacity-meta"><span><span className="capacity-live-dot"/>Updates every 15s · Last updated {new Date(data.as_of).toLocaleTimeString()}</span><span>Shared SQL ceiling <strong>{data.global_sql_limit}</strong></span></div>
      <div className="usage-summary">{fields.slice(0, 3).map(([key, label]) => {
        const active = data.active[key], limit = data.effective_limits[key], Icon = icons[key];
        return <article key={key} className={active >= limit ? 'capacity-full' : ''}><div className="usage-metric-top"><span className="capacity-icon"><Icon size={17} aria-hidden="true"/></span>{label}</div>
          <strong>{active == null ? 'Unavailable' : `${active} / ${limit}`}</strong>
          {active != null && <><progress aria-label={`${label} utilization`} max={limit} value={Math.min(active, limit)}/>
            <small>{active >= limit ? 'At capacity' : 'Available'} · {Math.round(active / limit * 100)}% used</small></>}
        </article>;
      })}</div>
      <div className="capacity-queue"><Clock3 size={15} aria-hidden="true"/><span>Reports waiting <strong>{data.report_queue.queued}</strong></span><span>Oldest wait <strong>{data.report_queue.oldest_wait_seconds}s</strong></span></div>
      <form className="policy-editor" onSubmit={save}>
        <div className="capacity-editor-heading"><h3>Company limits</h3><p>Changes apply to new work. Running tasks continue uninterrupted.</p></div>
        <div className="capacity-fields">{fields.map(([key, label, max]) => <label key={key}>{label}
          <input type="number" required min="1" max={max} step="1" disabled={busy} value={draft?.[key] ?? ''}
            onChange={event => setDraft(current => ({...current, [key]: event.target.value === '' ? '' : Number(event.target.value)}))}/>
          <small>{key === 'sql' ? `Effective ceiling: ${Math.min(Number(draft?.sql) || 1, data.global_sql_limit)} · shared server maximum ${data.global_sql_limit}` : key === 'sql_wait_seconds' ? 'Maximum time a SQL query waits for a free slot' : `Between 1 and ${max} simultaneous tasks`}</small>
        </label>)}</div>
        <details className="capacity-explainer"><summary><Info size={14} aria-hidden="true"/>How these limits work</summary><p>SQL capacity is capped by the global ceiling even if the company limit is higher. Chat analyses at capacity ask the user to retry; reports remain queued. SQL waits up to its timeout.</p>
        <p>Lowering a limit does not cancel running work. Existing work drains before the new ceiling is fully reflected. These are concurrency limits, not reserved capacity or CPU, memory, token, or billing quotas. Chat limits do not cover evaluation runs.</p></details>
        <div className="policy-actions"><button disabled={busy || !dirty}>Save limits</button>
          <button type="button" disabled={busy || !dirty} onClick={() => { setDraft(data.limits); setVersion(data.version); }}>Discard changes</button></div>
      </form>
    </>}
  </section>;
}
